import {
	CardElement,
	Elements,
	useElements,
	useStripe as useStripeElements,
} from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import { CreditCard, ExternalLink, Plus } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useApi } from "@/lib/fetch-client";
import { useStripe } from "@/lib/stripe";

import {
	CREDIT_TOP_UP_MAX_AMOUNT,
	CREDIT_TOP_UP_MIN_AMOUNT,
	isCreditTopUpAmountInRange,
} from "@llmgateway/shared";
import { formatNumber } from "@llmgateway/shared/number-format";
import { isOrganizationAdmin } from "@llmgateway/shared/organization-roles";

import type React from "react";

export function TopUpCreditsButton() {
	return (
		<TopUpCreditsDialog>
			<Button className="flex items-center">
				<Plus className="mr-2 h-4 w-4" />
				Top Up Credits
			</Button>
		</TopUpCreditsDialog>
	);
}

interface TopUpCreditsDialogProps {
	children?: React.ReactNode;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	// The organization receiving the credits.
	organizationId?: string;
}

export function TopUpCreditsDialog(props: TopUpCreditsDialogProps) {
	const api = useApi();
	const { data } = api.useQuery(
		"get",
		"/orgs",
		{
			params: { query: { includeChat: "true" } },
		},
		{ enabled: Boolean(props.organizationId) },
	);
	const organization = data?.organizations.find(
		(org) => org.id === props.organizationId,
	);
	if (!organization || !isOrganizationAdmin(organization.role)) {
		return null;
	}
	return (
		<BillingTopUpCreditsDialog
			key={organization.id}
			{...props}
			organizationId={organization.id}
		/>
	);
}

function BillingTopUpCreditsDialog({
	children,
	open: controlledOpen,
	onOpenChange: controlledOnOpenChange,
	organizationId,
}: TopUpCreditsDialogProps) {
	const [internalOpen, setInternalOpen] = useState(false);
	const open = controlledOpen ?? internalOpen;
	const setOpen = controlledOnOpenChange ?? setInternalOpen;
	const [step, setStep] = useState<
		"amount" | "payment" | "select-payment" | "confirm-payment" | "success"
	>("amount");
	const [amount, setAmount] = useState<number>(50);
	const [loading, setLoading] = useState(false);
	const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
		string | null
	>(null);
	const api = useApi();
	const queryClient = useQueryClient();
	// Stripe.js is only needed once the dialog is open; don't load it on the
	// pages that merely mount this dialog closed.
	const { stripe, isLoading: stripeLoading } = useStripe(open);

	const { data: paymentMethodsData, isLoading: paymentMethodsLoading } =
		api.useQuery(
			"get",
			"/payments/payment-methods",
			{ params: { query: { organizationId } } },
			{
				enabled: open, // Only fetch when dialog is open
			},
		);

	const hasPaymentMethods =
		paymentMethodsData?.paymentMethods &&
		paymentMethodsData.paymentMethods.length > 0;
	const defaultPaymentMethod = paymentMethodsData?.paymentMethods?.find(
		(pm) => pm.isDefault,
	);

	useEffect(() => {
		if (defaultPaymentMethod) {
			setSelectedPaymentMethod(defaultPaymentMethod.id);
		}
	}, [defaultPaymentMethod]);

	const handleClose = () => {
		setOpen(false);
		setTimeout(() => {
			setStep("amount");
			setLoading(false);
		}, 300);
	};

	const handlePaymentSuccess = async () => {
		// Invalidate organization query to refresh credits balance
		await queryClient.invalidateQueries({
			queryKey: api.queryOptions("get", "/orgs").queryKey,
		});
		setStep("success");
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			{children ? <DialogTrigger asChild>{children}</DialogTrigger> : null}
			<DialogContent className="sm:max-w-[500px]">
				{step === "amount" ? (
					<AmountStep
						amount={amount}
						setAmount={setAmount}
						organizationId={organizationId}
						onNext={() => {
							if (paymentMethodsLoading) {
								return; // Don't proceed if still loading
							}
							if (hasPaymentMethods) {
								setStep("select-payment");
							} else {
								setStep("payment");
							}
						}}
						onCancel={handleClose}
					/>
				) : step === "select-payment" ? (
					<SelectPaymentStep
						amount={amount}
						paymentMethods={paymentMethodsData?.paymentMethods ?? []}
						selectedPaymentMethod={selectedPaymentMethod}
						setSelectedPaymentMethod={setSelectedPaymentMethod}
						onUseSelected={() => setStep("confirm-payment")}
						onAddNew={() => setStep("payment")}
						onBack={() => setStep("amount")}
						onCancel={handleClose}
					/>
				) : step === "confirm-payment" ? (
					<ConfirmPaymentStep
						amount={amount}
						paymentMethodId={selectedPaymentMethod!}
						organizationId={organizationId}
						onSuccess={handlePaymentSuccess}
						onBack={() => setStep("select-payment")}
						onCancel={handleClose}
						setLoading={setLoading}
						loading={loading}
					/>
				) : step === "payment" ? (
					stripeLoading ? (
						<div className="p-6 text-center">Loading payment form...</div>
					) : (
						<Elements stripe={stripe as any}>
							<PaymentStep
								amount={amount}
								organizationId={organizationId}
								onBack={() => setStep("amount")}
								onSuccess={handlePaymentSuccess}
								onCancel={handleClose}
								setLoading={setLoading}
								loading={loading}
							/>
						</Elements>
					)
				) : (
					<SuccessStep onClose={handleClose} />
				)}
			</DialogContent>
		</Dialog>
	);
}

interface FeeData {
	baseAmount: number;
	platformFee: number;
	internationalFee: number;
	totalAmount: number;
}

function FeeBreakdownReceipt({
	feeData,
	feeDataLoading,
	amount,
}: {
	feeData: FeeData | undefined;
	feeDataLoading: boolean;
	/** Shown as a plain fallback when fee data is unavailable. */
	amount?: number;
}) {
	return (
		<div className="rounded-lg border border-dashed bg-muted/40 p-4 font-mono">
			<p className="mb-2 text-center text-[10px] font-semibold uppercase tracking-[0.3em] text-muted-foreground">
				· Lounge bar receipt ·
			</p>
			{feeDataLoading ? (
				<div className="flex items-center justify-center py-4">
					<div className="h-5 w-5 animate-spin border-2 border-muted-foreground border-t-transparent rounded-full" />
					<span className="ml-2 text-sm text-muted-foreground">
						Calculating fees...
					</span>
				</div>
			) : feeData ? (
				<div className="space-y-2 text-sm tabular-nums">
					<div className="flex justify-between">
						<span>Credits</span>
						<span>${feeData.baseAmount.toFixed(2)}</span>
					</div>
					<div className="flex justify-between">
						<span>Platform fee (5%)</span>
						<span>${feeData.platformFee.toFixed(2)}</span>
					</div>
					{feeData.internationalFee > 0 ? (
						<div className="flex justify-between">
							<span>Intl. card fee (1.5%)</span>
							<span>${feeData.internationalFee.toFixed(2)}</span>
						</div>
					) : null}
					<div className="flex justify-between border-t border-dashed pt-2 font-semibold">
						<span>Total</span>
						<span>${feeData.totalAmount.toFixed(2)}</span>
					</div>
				</div>
			) : amount !== undefined ? (
				<p className="text-sm text-muted-foreground">Amount: ${amount}</p>
			) : null}
		</div>
	);
}

function AmountStep({
	amount,
	setAmount,
	organizationId,
	onNext,
	onCancel,
}: {
	amount: number;
	setAmount: (amount: number) => void;
	organizationId?: string;
	onNext: () => void;
	onCancel: () => void;
}) {
	const presetAmounts = [10, 25, 50, 100];
	const api = useApi();
	const [checkoutLoading, setCheckoutLoading] = useState(false);
	const { mutateAsync: createCheckoutSession } = api.useMutation(
		"post",
		"/payments/create-checkout-session",
	);
	const isAmountValid = isCreditTopUpAmountInRange(amount);
	const amountValidationMessage =
		amount > CREDIT_TOP_UP_MAX_AMOUNT
			? `Maximum top-up amount is $${formatNumber(CREDIT_TOP_UP_MAX_AMOUNT)}.`
			: amount < CREDIT_TOP_UP_MIN_AMOUNT
				? `Minimum top-up amount is $${CREDIT_TOP_UP_MIN_AMOUNT}.`
				: !Number.isInteger(amount)
					? "Amount must be a whole dollar amount."
					: null;
	const { data: feeData, isLoading: feeDataLoading } = api.useQuery(
		"post",
		"/payments/calculate-fees",
		{
			body: { amount, organizationId },
		},
		{
			enabled: isAmountValid,
		},
	);
	const isActionDisabled =
		!isAmountValid || Boolean(feeDataLoading) || checkoutLoading;

	const handleStripeCheckout = async () => {
		setCheckoutLoading(true);
		try {
			const { checkoutUrl } = await createCheckoutSession({
				body: {
					amount,
					returnUrl: window.location.href.split("?")[0],
					...(organizationId ? { organizationId } : {}),
				},
			});
			window.location.href = checkoutUrl;
		} catch (error: unknown) {
			toast.error("Checkout Failed", {
				description:
					(error instanceof Error
						? error.message
						: (error as { message?: string } | undefined)?.message) ??
					"Failed to create checkout session.",
			});
			setCheckoutLoading(false);
		}
	};

	return (
		<>
			<DialogHeader>
				<p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-lounge-gold">
					<span aria-hidden className="h-px w-5 bg-lounge-gold/50" />
					The Lounge · Pay as you go
				</p>
				<DialogTitle className="font-display text-2xl font-semibold">
					Top Up Credits
				</DialogTitle>
				<DialogDescription>
					Add credits to your organization account. Confirm details on the next
					step.
				</DialogDescription>
			</DialogHeader>
			<div className="space-y-4 py-4">
				<div className="space-y-2">
					<Label htmlFor="amount">Amount (USD)</Label>
					<Input
						id="amount"
						type="number"
						min={CREDIT_TOP_UP_MIN_AMOUNT}
						max={CREDIT_TOP_UP_MAX_AMOUNT}
						step={1}
						value={amount}
						onChange={(e) => setAmount(Number(e.target.value))}
						required
					/>
					<p className="text-xs text-muted-foreground">
						Minimum ${CREDIT_TOP_UP_MIN_AMOUNT}. Maximum $
						{formatNumber(CREDIT_TOP_UP_MAX_AMOUNT)}.
					</p>
					{amountValidationMessage ? (
						<p className="text-xs text-destructive">
							{amountValidationMessage}
						</p>
					) : null}
				</div>
				<div className="flex flex-wrap gap-2">
					{presetAmounts.map((preset) => (
						<Button
							key={preset}
							type="button"
							variant="outline"
							onClick={() => setAmount(preset)}
						>
							${preset}
						</Button>
					))}
				</div>

				{isAmountValid && (
					<FeeBreakdownReceipt
						feeData={feeData}
						feeDataLoading={Boolean(feeDataLoading)}
					/>
				)}
			</div>
			<DialogFooter className="flex flex-col gap-3 sm:flex-col">
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" onClick={onCancel}>
						Cancel
					</Button>
					<Button type="button" onClick={onNext} disabled={isActionDisabled}>
						Pay with Card
					</Button>
				</div>
				<div className="relative">
					<div className="absolute inset-0 flex items-center">
						<span className="w-full border-t" />
					</div>
					<div className="relative flex justify-center text-xs uppercase">
						<span className="bg-background px-2 text-muted-foreground">or</span>
					</div>
				</div>
				<Button
					type="button"
					variant="outline"
					className="w-full"
					onClick={handleStripeCheckout}
					disabled={isActionDisabled}
				>
					{checkoutLoading ? (
						"Redirecting..."
					) : (
						<>
							<ExternalLink className="mr-2 h-4 w-4" />
							Pay with Stripe Checkout
						</>
					)}
				</Button>
				<p className="text-xs text-muted-foreground text-center">
					Stripe Checkout supports additional payment methods like Google Pay,
					Apple Pay, and more.
				</p>
				<InvoiceSettingsNote />
			</DialogFooter>
		</>
	);
}

function SuccessStep({ onClose }: { onClose: () => void }) {
	const reduceMotion = useReducedMotion();
	return (
		<>
			<DialogHeader>
				<DialogTitle className="font-display text-2xl font-semibold">
					Payment Successful
				</DialogTitle>
				<DialogDescription>
					Your credits have been added to your account.
				</DialogDescription>
			</DialogHeader>
			<div className="flex flex-col items-center gap-4 py-4">
				<motion.span
					aria-hidden
					initial={
						reduceMotion
							? { opacity: 0 }
							: { opacity: 0, scale: 2, rotate: -16 }
					}
					animate={
						reduceMotion ? { opacity: 1 } : { opacity: 1, scale: 1, rotate: -6 }
					}
					transition={
						reduceMotion
							? { duration: 0.2, ease: "easeOut" }
							: { type: "spring", duration: 0.4 }
					}
					className="rounded border-2 border-lounge-gold/60 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.3em] text-lounge-gold"
				>
					Credits added
				</motion.span>
				<p className="text-center">
					Thank you for your purchase. Your credits are now available for use.
				</p>
			</div>
			<DialogFooter>
				<Button onClick={onClose}>Close</Button>
			</DialogFooter>
		</>
	);
}

function PaymentStep({
	amount,
	organizationId,
	onBack,
	onSuccess,
	onCancel,
	loading,
	setLoading,
}: {
	amount: number;
	organizationId?: string;
	onBack: () => void;
	onSuccess: () => Promise<void> | void;
	onCancel: () => void;
	loading: boolean;
	setLoading: (loading: boolean) => void;
}) {
	const stripe = useStripeElements();
	const elements = useElements();
	const api = useApi();
	const { mutateAsync: topUpMutation } = api.useMutation(
		"post",
		"/payments/create-payment-intent",
	);
	const { mutateAsync: setupIntentMutation } = api.useMutation(
		"post",
		"/payments/create-setup-intent",
	);

	const [saveCard, setSaveCard] = useState(true);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!stripe || !elements) {
			return;
		}

		setLoading(true);

		try {
			let stripePaymentMethodId: string | undefined;

			if (saveCard) {
				const { clientSecret: setupSecret } = await setupIntentMutation({
					body: { organizationId },
				});

				const setupResult = await stripe.confirmCardSetup(setupSecret, {
					payment_method: {
						card: elements.getElement(CardElement) as any,
					},
				});

				if (setupResult.error) {
					toast.error("Error Saving Card", {
						description: setupResult.error.message,
					});
					setLoading(false);
					return;
				}

				const setupPaymentMethod = setupResult.setupIntent?.payment_method;
				stripePaymentMethodId =
					typeof setupPaymentMethod === "string"
						? setupPaymentMethod
						: setupPaymentMethod?.id;
			} else {
				const pmResult = await stripe.createPaymentMethod({
					type: "card",
					card: elements.getElement(CardElement) as any,
				});

				if (pmResult.error) {
					toast.error("Error", {
						description:
							pmResult.error.message ?? "Could not read card details.",
					});
					setLoading(false);
					return;
				}

				stripePaymentMethodId = pmResult.paymentMethod.id;
			}

			const { clientSecret } = await topUpMutation({
				body: {
					amount,
					stripePaymentMethodId,
					...(organizationId ? { organizationId } : {}),
				},
			});

			const result = await stripe.confirmCardPayment(clientSecret);

			if (result.error) {
				toast.error("Payment Failed", {
					description: result.error.message,
				});
				setLoading(false);
			} else {
				await onSuccess();
			}
		} catch (error: any) {
			toast.error("Payment Failed", {
				description:
					error?.message ?? "An error occurred while processing your payment.",
			});
			setLoading(false);
		}
	};

	return (
		<>
			<DialogHeader>
				<DialogTitle>Payment Details</DialogTitle>
				<DialogDescription>
					Enter your card details to add ${amount} credits.
				</DialogDescription>
			</DialogHeader>
			<form onSubmit={handleSubmit} className="space-y-4 py-4">
				<div className="space-y-2">
					<Label htmlFor="card-element">Card Details</Label>
					<div className="border rounded-md p-3">
						<CardElement
							id="card-element"
							options={{
								style: {
									base: {
										fontSize: "16px",
										color: "#424770",
										"::placeholder": {
											color: "#aab7c4",
										},
									},
									invalid: {
										color: "#9e2146",
									},
								},
							}}
						/>
					</div>
				</div>
				<div className="space-y-2">
					<div className="flex items-center space-x-2">
						<Checkbox
							id="save-card"
							checked={saveCard}
							onCheckedChange={(checked) => setSaveCard(checked as boolean)}
						/>
						<Label htmlFor="save-card">
							Save this card for future payments
						</Label>
					</div>
				</div>
				<p className="text-xs text-muted-foreground">
					International cards are subject to an additional 1.5% processing fee.
				</p>
				<DialogFooter className="flex space-x-2 justify-end">
					<Button
						type="button"
						variant="outline"
						onClick={onBack}
						disabled={loading}
					>
						Back
					</Button>
					<Button
						type="button"
						variant="outline"
						onClick={onCancel}
						disabled={loading}
					>
						Cancel
					</Button>
					<Button type="submit" disabled={!stripe || loading}>
						{loading ? "Processing..." : `Continue`}
					</Button>
				</DialogFooter>
				<InvoiceSettingsNote />
			</form>
		</>
	);
}

function SelectPaymentStep({
	amount,
	paymentMethods,
	selectedPaymentMethod,
	setSelectedPaymentMethod,
	onUseSelected,
	onAddNew,
	onBack,
	onCancel,
}: {
	amount: number;
	paymentMethods: {
		id: string;
		stripePaymentMethodId: string;
		type: string;
		isDefault: boolean;
		cardBrand?: string;
		cardLast4?: string;
		expiryMonth?: number;
		expiryYear?: number;
	}[];
	selectedPaymentMethod: string | null;
	setSelectedPaymentMethod: (id: string) => void;
	onUseSelected: () => void;
	onAddNew: () => void;
	onBack: () => void;
	onCancel: () => void;
}) {
	return (
		<>
			<DialogHeader>
				<DialogTitle>Select Payment Method</DialogTitle>
				<DialogDescription>
					Choose a payment method to add ${amount} credits. Confirm details on
					the next step.
				</DialogDescription>
			</DialogHeader>
			<div className="space-y-4 py-4">
				<div className="space-y-2">
					{paymentMethods.map((method) => (
						<div
							key={method.id}
							className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer ${
								selectedPaymentMethod === method.id ? "border-primary" : ""
							}`}
							onClick={() => setSelectedPaymentMethod(method.id)}
						>
							<div className="flex items-center gap-3">
								<CreditCard className="h-5 w-5" />
								<div>
									<p>
										{method.cardBrand} •••• {method.cardLast4}
									</p>
									<p className="text-sm text-muted-foreground">
										Expires {method.expiryMonth}/{method.expiryYear}
									</p>
								</div>
								{method.isDefault && (
									<span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
										Default
									</span>
								)}
							</div>
						</div>
					))}
					<Button
						variant="outline"
						className="w-full flex items-center justify-center gap-2"
						onClick={onAddNew}
					>
						<Plus className="h-4 w-4" />
						Add New Payment Method
					</Button>
				</div>
			</div>
			<DialogFooter className="flex space-x-2 justify-end">
				<Button type="button" variant="outline" onClick={onBack}>
					Back
				</Button>
				<Button type="button" variant="outline" onClick={onCancel}>
					Cancel
				</Button>
				<Button
					type="button"
					onClick={onUseSelected}
					disabled={!selectedPaymentMethod}
				>
					Continue
				</Button>
			</DialogFooter>
		</>
	);
}

function ConfirmPaymentStep({
	amount,
	paymentMethodId,
	organizationId,
	onSuccess,
	onBack,
	onCancel,
	loading,
	setLoading,
}: {
	amount: number;
	paymentMethodId: string;
	organizationId?: string;
	onSuccess: () => Promise<void> | void;
	onBack: () => void;
	onCancel: () => void;
	loading: boolean;
	setLoading: (loading: boolean) => void;
}) {
	const api = useApi();
	const { mutateAsync: topUpMutation } = api.useMutation(
		"post",
		"/payments/top-up-with-saved-method",
	);

	const { data: feeData, isLoading: feeDataLoading } = api.useQuery(
		"post",
		"/payments/calculate-fees",
		{
			body: { amount, paymentMethodId, organizationId },
		},
	);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		setLoading(true);

		try {
			await topUpMutation({
				body: {
					amount,
					paymentMethodId,
					...(organizationId ? { organizationId } : {}),
				},
			});
			await onSuccess();
		} catch (error: any) {
			toast.error("Payment Failed", {
				description:
					error?.message ?? "An error occurred while processing your payment.",
			});
			setLoading(false);
		}
	};

	return (
		<>
			<DialogHeader>
				<DialogTitle>Confirm Payment</DialogTitle>
				<DialogDescription>
					Review your payment details before confirming.
				</DialogDescription>
			</DialogHeader>
			<form onSubmit={handleSubmit} className="space-y-4 py-4">
				<FeeBreakdownReceipt
					feeData={feeData}
					feeDataLoading={Boolean(feeDataLoading)}
					amount={amount}
				/>
				<DialogFooter className="flex space-x-2 justify-end">
					<Button
						type="button"
						variant="outline"
						onClick={onBack}
						disabled={loading}
					>
						Back
					</Button>
					<Button
						type="button"
						variant="outline"
						onClick={onCancel}
						disabled={loading}
					>
						Cancel
					</Button>
					<Button type="submit" disabled={loading || feeDataLoading}>
						{loading
							? "Processing..."
							: `Pay ${feeData ? `$${feeData.totalAmount.toFixed(2)}` : `$${amount}`}`}
					</Button>
				</DialogFooter>
				<InvoiceSettingsNote />
			</form>
		</>
	);
}

function InvoiceSettingsNote() {
	return (
		<p className="text-center text-[11px] leading-relaxed text-muted-foreground">
			Need company/address details on your invoice? Update billing settings
			before purchase. We email the invoice automatically after payment.
		</p>
	);
}
